import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { partition } from 'lodash'
import { useFocusEffect } from '@react-navigation/native'
import { NavigationContainer } from '@screens/Common/containers'
import { Text } from '@components/base'
import { healthDataReportsSelector } from '@screens/Nora/models/nora.selectors'
import { StyleService, useStyleSheet } from '@style/service'
import { HealthDataReportSortField, SortDirection } from '@src/types'
import { useGoBack } from '@src/utils/navigation'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { ReportCard } from '../components/ReportCard'
import { isReportUnread } from '../utils'

export const NoraReportsScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const goBack = useGoBack()
  const dispatch = useDispatch()
  const healthDataReports = useSelector(healthDataReportsSelector)

  const [unreadReports, previousReports] = useMemo(
    () => partition(healthDataReports, (report) => isReportUnread(report)),
    [healthDataReports],
  )

  useFocusEffect(
    useCallback(() => {
      dispatch({
        type: 'nora/allHealthDataReports',
        payload: {
          sorts: [
            {
              direction: SortDirection.Desc,
              field: HealthDataReportSortField.CreatedAt,
            },
          ],
        },
      })
    }, [dispatch]),
  )

  return (
    <NavigationContainer title="Reports" goBack={goBack}>
      <ScrollView
        style={styles.contentContainer}
        contentContainerStyle={{ paddingBottom: insets.bottom }}
      >
        {unreadReports.length > 0 && (
          <View style={styles.section}>
            <Text type="small" bold style={styles.sectionLabel}>
              Unread Reports
            </Text>
            {unreadReports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </View>
        )}
        {previousReports.length > 0 && (
          <View style={styles.section}>
            <Text type="small" bold style={styles.sectionLabel}>
              Previous Reports
            </Text>
            {previousReports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </View>
        )}
        {healthDataReports.length === 0 && (
          <Text type="regular" style={styles.noResults}>
            No reports yet
          </Text>
        )}
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  contentContainer: {
    flex: 1,
    padding: 16,
    gap: 12,
  },
  section: {
    gap: 8,
    marginBottom: 16,
  },
  sectionLabel: {
    color: 'theme.text.tertiary',
  },
  noResults: {
    textAlign: 'center',
    color: 'theme.text.secondary',
    marginTop: 32,
  },
})
